import { DateTime, Duration } from 'luxon'
import ms from 'milliseconds'

import { useApiQuery } from '~/src/IO/Api'
import { getDateTime, getTimeParams } from '~/src/Lib/Utils'
import { useEntityQuery } from '~/src/Store/hooks/useEntityQuery'

import { CONTROL_DAILY_JOURNAL_EVENT_TYPE } from '../constants'

const {
  EMERGENCY_SHOT,
  IRRIGATION_COMPLETED,
  P2_STARTED_DRAINAGE,
  P2_STARTED_TIME,
  RUN_ONCE,
  SCHEDULE_DELETED,
} = CONTROL_DAILY_JOURNAL_EVENT_TYPE

const INVALID_DATE_TIME = DateTime.invalid('A valid DateTime is required.')
const MAX_EXPECTED_SCHEDULE_DURATION = { hours: 8 }

const automatedIrrigationEventsPath = '/automatedIrrigationEvents/'
const controlDailyJournalsEventTypes = [
  EMERGENCY_SHOT,
  IRRIGATION_COMPLETED,
  P2_STARTED_DRAINAGE,
  P2_STARTED_TIME,
  RUN_ONCE,
  SCHEDULE_DELETED,
]
const controlDailyJournalsPath = '/controlDailyJournals/'
export const controllerSchedulePublishHistoriesPath = '/controllerSchedulePublishHistories/'
export const getFutureExpectedOpenTimesPath = '/irrigationSchedules/get_future_expected_open_times/'
const irrigationPortStatusLogHistoryPath = '/irrigationPortStatusLogHistory/'

// spacer and staggerRefetches helps to space out data updates to reduce thrashing
// by staggering refetches by adding an incrementing seconds step to refetchInterval
// individual increment steps are tracked by requested minute interval
// Example:
// staggerRefetches() sets refetchInterval to 5 minutes + 2 seconds
// staggerRefetches() sets refetchInterval to 5 minutes + 4 seconds
// staggerRefetches(1) sets refetchInterval to 1 minutes + 2 seconds
const spacer = {
  $step: {},
  step(interval) {
    this.$step[interval] ??= 0
    this.$step[interval] += 2
    return this.$step[interval]
  },
  reset() {
    this.$step = {}
  }
}
const staggerRefetches = (minutes = 5) => ms.minutes(minutes) + ms.seconds(spacer.step(minutes))

const useFacilityIrrigationQueries = ({
  endDT = INVALID_DATE_TIME,
  startDT = INVALID_DATE_TIME,
  zoneIds = [],
}) => {
  // reset spacer step to prevent fetches becoming increasingly delayed
  spacer.reset()
  const startOfMinute = getDateTime('now').startOf('minute')
  // Overfetch history to ensure event groups straddling `startDT` display all events.
  const eventHistoryBuffer = Duration.fromObject(MAX_EXPECTED_SCHEDULE_DURATION)

  const hasValidDateParams = startDT.isValid && endDT.isValid

  // Updates device last communication times for emergency shot / AUTOMAIT degraded state detection
  // This replaces the dataReadings fetches in useIrrigationScheduleErrorConditions
  useEntityQuery({
    entityKey: 'devices',
    params: {
      modelFilter: 'ter15',
      pagination: 0,
    },
    queryOptions: { refetchInterval: staggerRefetches() },
    returnRaw: true,
  })
  useEntityQuery({
    entityKey: 'devices',
    params: {
      modelFilter: 'drain',
      pagination: 0,
    },
    queryOptions: { refetchInterval: staggerRefetches() },
    returnRaw: true,
  })

  const automatedIrrigationEvents = useApiQuery({
    path: automatedIrrigationEventsPath,
    params: {
      end: endDT.toISO(),
      pagination: 0,
      start: startDT.toISO(),
    },
    queryKeyOverride: [automatedIrrigationEventsPath],
    queryOptions: {
      enabled: hasValidDateParams,
      refetchInterval: staggerRefetches(),
    },
  })

  const controlDailyJournals = useEntityQuery({
    entityKey: 'controlDailyJournals',
    params: {
      end: endDT.toISO(),
      // eslint-disable-next-line babel/camelcase
      event_types: controlDailyJournalsEventTypes,
      pagination: 0,
      start: startDT.toISO(),
      zones: zoneIds.length ? zoneIds : undefined,
    },
    queryKeyOverride: [controlDailyJournalsPath, controlDailyJournalsEventTypes, zoneIds],
    queryOptions: {
      enabled: hasValidDateParams,
      refetchInterval: staggerRefetches(),
    },
    returnRaw: true,
  })

  const eventHistory = useApiQuery({
    path: irrigationPortStatusLogHistoryPath,
    params: {
      /* eslint-disable babel/camelcase */
      end__lte: endDT.toISO(),
      ordering: 'start',
      pagination: 0,
      start__gte: startDT.minus(eventHistoryBuffer).toISO(),
      zones: zoneIds.length ? zoneIds : undefined,
      /* eslint-enable babel/camelcase */
    },
    queryKeyOverride: [irrigationPortStatusLogHistoryPath, zoneIds],
    queryOptions: {
      enabled: hasValidDateParams,
      refetchInterval: staggerRefetches(1),
    },
  })

  const futureExpectedEvents = useApiQuery({
    path: getFutureExpectedOpenTimesPath,
    params: {
      /* eslint-disable babel/camelcase */
      end_date: endDT.toISODate(),
      start_date: startOfMinute.toISODate(),
      zones: zoneIds.length ? zoneIds : undefined,
      /* eslint-enable babel/camelcase */
    },
    queryFn: ({ apiFetch, params, path }) => apiFetch(path, params, { method: 'POST' })
      .then(response => response.map(event => ({
        ...event,
        schedule: event.scheduleId,
      }))),
    queryKeyOverride: [getFutureExpectedOpenTimesPath, zoneIds],
    queryOptions: {
      enabled: hasValidDateParams,
      refetchInterval: staggerRefetches(1),
    },
  })

  const irrigationScheduleHistories = useApiQuery({
    path: controllerSchedulePublishHistoriesPath,
    params: {
      end: endDT.toISO(),
      ordering: '-publish_on',
      pagination: 0,
      // This endpoint includes the schedule publish entity immediately preceding `start`, regardless of time gap.
      start: startDT.minus(eventHistoryBuffer).toISO(),
    },
    queryFn: ({ apiFetch, params, path }) => apiFetch(path, params).then(response => response.reduce((histories, schedulePublishHistory) => {
      const { lastPublishedSchedules, publishOn, publishedPrograms: publishedProgramsRaw } = schedulePublishHistory
      Object.values(lastPublishedSchedules).forEach(published => {
        const { automatedScheduleSettings, isAutomatedSchedule, programIds, scheduleId, scheduleStartTime } = published
        if (programIds === undefined) return  // Old, incompatible history entity
        const publishedPrograms = programIds.map(programId => {
          const rawProgram = publishedProgramsRaw.find(p => p.id === programId)
          if (!rawProgram) return null
          const {
            d: channelDurations,  // Array of durations for each (ordered) controller channel, in seconds
            r: repeats,           // Number of repeats after initial event
            s: start,             // ISO standard datetime string
          } = rawProgram
          return {
            count: 1 + repeats,
            duration: Math.max(...channelDurations),
            start,
          }
        })
        histories[scheduleId] ??= []
        histories[scheduleId].push({
          automatedScheduleSettings: Object.values(automatedScheduleSettings)[0] ?? {},
          isAutomatedSchedule,
          publishedPrograms,
          publishOn: getDateTime(publishOn).toMillis(),
          scheduleStartTimeParams: scheduleStartTime && getTimeParams(getDateTime(scheduleStartTime)),
        })
      })
      return histories
    }, {})),
    queryKeyOverride: [controllerSchedulePublishHistoriesPath, zoneIds],
    queryOptions: {
      enabled: hasValidDateParams,
      refetchInterval: staggerRefetches(),
    },
  })

  return {
    automatedIrrigationEvents,
    controlDailyJournals,
    eventHistory,
    futureExpectedEvents,
    irrigationScheduleHistories,
  }
}

export default useFacilityIrrigationQueries
